import { useTranslation } from 'react-i18next';

import { LoggedInTeaserElement } from '@shared/dream-components';

import {
  AccountProfileProvider,
  BillingProvider,
  PreferencesProvider,
  ReferralsProvider,
  SubscriptionActionsProvider,
  useDevScenarioContext,
  useModalCoordinationContext,
} from '~/context/SubscriptionManagement';
import type { Community, CurrentCommunityMember } from '~/interfaces/community';
import type { Publication } from '~/interfaces/publication';
import type { Site } from '~/interfaces/site';
import type { Subscription } from '~/interfaces/subscriptions';

import { AccountProfileModals } from './AccountProfileModals';
import { AccountProfileSection } from './AccountProfileSection';
import { BillingSection } from './BillingSection';
import { CommunitySection } from './CommunitySection';
import { CurrentPlansSection } from './CurrentPlansSection';
import { GeneralScenarioPicker } from './GeneralScenarioPicker';
import { PreferencesSection } from './PreferencesSection';
import { ReferralsSection } from './ReferralsSection';
import { SubscriptionLifecycleModals } from './SubscriptionLifecycleModals';

interface Props {
  site: Site;
  publication: Publication;
  subscription: Subscription;
  subscriberId: string;
  isLoggedIn: boolean;
  community?: Community | null;
  currentCommunityMember?: CurrentCommunityMember | null;
}

export const General = ({
  site,
  publication,
  subscription,
  subscriberId,
  isLoggedIn,
  community,
  currentCommunityMember,
}: Props) => {
  const { t } = useTranslation();

  // Only used by the logged-out teaser's translation gate below; the billing surface itself
  // (and the rest of the billing slice) now lives in BillingProvider / BillingSection.
  const isPremiumTier = Array.isArray(subscription.premium_tiers) && subscription.premium_tiers.length > 0;

  // DEV-only General-tab scenario override (BEE-18984). Gated on the group-subscriptions Flipper
  // flag (BEE-18968); the scenario it applies feeds the General tab's scenario-adjusted
  // subscription + group list via DevScenarioProvider / EffectiveGroupSubscriptionsProvider.
  const groupSubscriptionsEnabled = publication.group_subscriptions_enabled ?? false;
  const { scenario: generalScenario, setScenario: setGeneralScenario } = useDevScenarioContext();

  const {
    global: { handleOpenModal: handleOpenGlobalModal },
  } = useModalCoordinationContext();

  return (
    <>
      {import.meta.env.DEV && isLoggedIn && groupSubscriptionsEnabled && (
        <GeneralScenarioPicker value={generalScenario} onChange={setGeneralScenario} />
      )}

      <AccountProfileProvider
        subscription={subscription}
        site={site}
        subscriberId={subscriberId}
        isLoggedIn={isLoggedIn}
      >
        <AccountProfileModals />

        <AccountProfileSection />
      </AccountProfileProvider>

      <SubscriptionActionsProvider
        subscription={subscription}
        site={site}
        subscriberId={subscriberId}
        isLoggedIn={isLoggedIn}
      >
        <CurrentPlansSection />

        <div className="absolute">
          <SubscriptionLifecycleModals />
        </div>
      </SubscriptionActionsProvider>

      <CommunitySection
        community={community}
        currentCommunityMember={currentCommunityMember}
        subscription={subscription}
      />

      {!isLoggedIn && (
        <ReferralsProvider subscription={subscription} publication={publication} subscriberId={subscriberId}>
          <ReferralsSection />
        </ReferralsProvider>
      )}

      {!isLoggedIn && (
        <LoggedInTeaserElement
          element={{
            type: 'loggedInTeaser',
          }}
          onClickButton={() => handleOpenGlobalModal('login', { preventScrollReset: true })}
          buttonLabel={t('shared.cta.login_to_continue')}
          translations={{
            ...(isPremiumTier
              ? {
                  'billing.current_plan.title': t('billing.current_plan.title'),
                  'billing.summary.empty_state': t('billing.summary.empty_state'),
                  'billing.summary.title': t('billing.summary.title'),
                  'billing.summary.last_payment_heading': t('billing.summary.last_payment_heading'),
                }
              : {}),
            'manage.preferences.title': t('manage.preferences.title'),
            'manage.preferences.description': t('manage.preferences.description'),
          }}
        >
          {null}
        </LoggedInTeaserElement>
      )}

      <BillingProvider subscription={subscription} site={site} subscriberId={subscriberId} isLoggedIn={isLoggedIn}>
        <BillingSection />
      </BillingProvider>

      <PreferencesProvider subscription={subscription} subscriberId={subscriberId} isLoggedIn={isLoggedIn}>
        <PreferencesSection />
      </PreferencesProvider>

      {isLoggedIn && (
        <ReferralsProvider subscription={subscription} publication={publication} subscriberId={subscriberId}>
          <ReferralsSection />
        </ReferralsProvider>
      )}
    </>
  );
};
